// Companions playing together: file basketball and the party. Each script takes the run
// tokens of everyone in it (see runGroup in runtime/index.js); a companion that gets interrupted,
// say picked up, simply drops out.

import { clockNow } from './clock.js';
import { env, clamp, rand, chance, lerp, easeInOut, weighted, tick, wait, animate, isCurrent } from './core.js';
import { cast, pulse, setShape, applyRest } from './companion.js';
import { PARTY_COLORS, STAND, SPEAKER_ROWS, NOTES } from './props.js';
import { travelTo, walkOn, arrive, baseOf } from './travel.js';
import { surfaceBelow, FLOOR, floorY, castleLeft, floorRest, floorStretches, restsOnFloor } from './world.js';
import { props } from './stage.js';
import * as fx from './particles.js';
import { can } from './catalog.js';
import { hooks, happyHop, goNear, standHere, unfold, fold } from './acts.js';

// A smooth up-and-down over one beat (0 at the start and end of the beat, 1 in the middle).
const hump = f => Math.sin(Math.PI * clamp((f - 0.1) / 0.8, 0, 1));

// ---------- file basketball ----------
// The thrower (Clawd) shoots files at the catcher (the File Explorer) from wherever they both
// are: across a window, up onto a toolbar, down from a title bar. The catcher opens up and runs
// to where the file will come down, with a dive at the end if it's close. It doesn't always make
// it: a missed file bounces off the rim or drops onto whatever is below.
const GRAVITY = 300;            // crab pixels per second², times the size
const CATCH = 4.5, RIM = 7;     // how far from the catcher's middle a file goes in, or hits the rim

// The middle of the catcher's open top, where a file goes in.
function mouthOf(me) {
  const top = me.rig ? me.rig.top : -(me.shape.bh + me.shape.leg);
  return { x: me.x, y: me.y - me.jump + me.bob + (top + 2) * me.s };
}

// Walk along a surface and stand at x there.
async function walkTo(ledge, x, g) {
  const me = g.me, geo = ledge.geom();
  if (!geo) return;
  me.anchor = null; me.moving = true;
  const ok = await walkOn(ledge, x, g);
  const now = ledge.geom();
  if (ok && now) arrive(me, { ledge, dx: x - now.x1, rest: 'stand' });
  else me.moving = false;
}

export async function basketball(gA, gB) {
  const A = gA.me, B = gB.me, s = env.S;
  standHere(A); standHere(B);
  if (Math.hypot(B.x - A.x, B.y - A.y) < 20 * s) { await goNear(gB); standHere(B); }   // too close to throw
  const face = () => {
    const dir = Math.sign(B.x - A.x) || 1;
    A.pose.eyes = dir > 0 ? 'right' : 'left'; B.pose.eyes = dir > 0 ? 'left' : 'right';
  };
  face();
  await Promise.all([happyHop(gA, 1), happyHop(gB, 1)]);
  B.pose.variant = 'open';
  pulse(B, 'stretch');
  await wait(400, gA);
  const shots = 3 + Math.floor(rand(0, 3));
  let made = 0;
  for (let k = 0; k < shots; k++) {
    face();
    if (await shoot(gA, gB)) made++;
    await wait(rand(300, 600), gA);
    // Now and then the catcher moves somewhere else, so the next shot is a new one.
    if (k < shots - 1 && chance(0.3)) {
      B.pose.variant = null;
      await goNear(gB);
      standHere(B);
      B.pose.variant = 'open';
    }
  }
  // The end: both cheer, more if most went in.
  B.pose.variant = null;
  const hearts = made >= shots - 1 ? 6 : 3;
  for (let k = 0; k < hearts; k++) fx.heart(lerp(A.x, B.x, 0.5) + rand(-8, 8) * s, Math.min(A.y, B.y) - 10 * s, s);
  await Promise.all([happyHop(gA, 2), happyHop(gB, 2)]);
}

// One shot. Returns true when the file went in.
async function shoot(gA, gB) {
  const A = gA.me, B = gB.me, s = env.S;
  const dir = Math.sign(B.x - A.x) || 1, arm = dir > 0 ? 'R' : 'L', key = 'arm' + arm;
  // Take a file and dribble it once.
  Object.assign(A.pose, { holdArm: arm, hold: 'file', [key]: 'hold' });
  await wait(300, gA);
  const hand = () => { const h = A.rig?.hand[arm] || [dir * 8, -3]; return { x: A.x + (h[0] + 0.5) * A.s, y: A.y + h[1] * A.s }; };
  const h0 = hand();
  const file = { kind: 'file', owner: A, x: h0.x, y: h0.y, s, rot: 0 };
  A.pose.hold = null; props.add(file);
  await animate(360, gA, t => { file.x = h0.x; file.y = h0.y + (A.y - 1 - h0.y) * Math.sin(t * Math.PI); });

  // Aim a high arc at the catcher's open top, never quite perfectly: mostly close, now and
  // then way off, and longer shots miss by more.
  A.pose[key] = 'reach';
  const start = hand(), goal = mouthOf(B), g = GRAVITY * s;
  const dist = Math.hypot(goal.x - start.x, goal.y - start.y);
  const aimX = goal.x + (Math.random() + Math.random() - 1) * (5 * s + 0.15 * Math.abs(goal.x - start.x));
  const apexY = Math.max(4 * s, Math.min(start.y, goal.y) - clamp(0.3 * dist, 16 * s, 60 * s));
  const rise = Math.max(1, start.y - apexY), fall = Math.max(1, goal.y - apexY);
  const T = Math.sqrt(2 * rise / g) + Math.sqrt(2 * fall / g);
  const ball = { x: start.x, y: start.y, vx: (aimX - start.x) / T, vy: -Math.sqrt(2 * g * rise) };
  const result = await flight(gA, gB, file, ball, chance(0.1));
  A.pose[key] = 'side';

  if (result === 'in') {                                  // swish: the folder gulps it
    props.delete(file);
    fx.burst(file.x, file.y, s, ['#FFFFFF', '#C9D3E0', '#FFD23F'], 10, 6, 40, 500);
    B.pose.variant = null; pulse(B, 'squash'); await wait(160, gB);
    B.pose.variant = 'open';
    await Promise.all([happyHop(gB, 1), (async () => { A.pose.armL = A.pose.armR = 'up'; await wait(400, gA); A.pose.armL = A.pose.armR = 'side'; })()]);
    return true;
  }
  if (result?.landed && isCurrent(gB) && B.anchor && result.landed.ledge.id === baseOf(B.anchor).id) {
    // It landed where the catcher stands: it goes and picks it up.
    B.pose.variant = null;
    B.pose.eyes = result.x < B.x ? 'left' : 'right';
    await walkTo(baseOf(B.anchor), result.x - Math.sign(result.x - B.x) * 2 * s, gB);
    B.pose.variant = 'open'; await wait(200, gB);
    props.delete(file); fx.burst(file.x, file.y, s, ['#FFFFFF', '#C9D3E0'], 6, 4, 30, 300);
    B.pose.variant = null; await wait(150, gB); B.pose.variant = 'open';
    return false;
  }
  // Somewhere else, or off the screen: it lies there a moment, then crumples away.
  A.pose.eyes = 'down';
  if (result?.landed) { file.rot = 0; await wait(900, gA); fx.burst(file.x, file.y, s, ['#FFFFFF', '#C9D3E0', '#9AA7B8'], 8, 5, 30, 400); }
  props.delete(file);
  if (isCurrent(gB)) B.pose.variant = 'open';
  return false;
}

// The file flies under gravity while the catcher runs under it. Like a flying companion it
// passes in front of everything on the way; it can only land once it has come down past the
// catcher (missed, or bounced off the rim). Returns 'in', 'gone' (off the screen) or
// { landed: { ledge, g }, x } where it came to rest.
async function flight(gA, gB, file, ball, fumble) {
  const B = gB.me, s = env.S, g = GRAVITY * s, react = rand(0.15, 0.3);
  let last = clockNow(), t = 0, rimmed = false, past = false, ground = null, nextGround = 0, dive = null, legT = 0, legB = false;
  for (;;) {
    await tick(gA);
    const now = clockNow(), dt = Math.min(0.05, (now - last) / 1000);
    last = now; t += dt;
    const prevY = ball.y;
    ball.vy += g * dt; ball.x += ball.vx * dt; ball.y += ball.vy * dt;
    file.x = ball.x; file.y = ball.y; file.rot += 9 * dt * (Math.sign(ball.vx) || 1);

    const catcher = isCurrent(gB) && B.anchor && !rimmed;
    if (catcher) {
      const m = mouthOf(B);
      // Where the file will come down to the catcher's height, and how soon.
      const disc = ball.vy * ball.vy - 2 * g * (ball.y - m.y);
      const left = disc >= 0 ? (-ball.vy + Math.sqrt(disc)) / g : 0;
      const geo = B.anchor.ledge.geom();
      if (geo && t > react && B.anchor.dx !== undefined) {
        const right = (B.anchor.ledge === FLOOR ? castleLeft() : geo.x2) - 6 * B.s, lo = geo.x1 + 6 * B.s;
        const want = lo <= right ? clamp(ball.x + ball.vx * left, lo, right) : B.x, d = want - B.x;
        if (!dive && left < 0.32 && Math.abs(d) > CATCH * s && Math.abs(d) < 14 * s) dive = now;   // a dive for it
        const speed = (dive ? 70 : 32) * B.s, stepX = Math.sign(d) * Math.min(Math.abs(d), speed * dt);
        B.anchor.dx += stepX;
        legT += dt;
        if (Math.abs(stepX) > 0.1 && legT > 0.08) { legT = 0; legB = !legB; B.pose.legs = legB ? 'walkB' : 'walkA'; }
        B.pose.eyes = ball.x < B.x - 3 * s ? 'left' : ball.x > B.x + 3 * s ? 'right' : 'up';
      }
      if (dive) { const k = (now - dive) / 320; B.jump = k < 1 ? 4 * 3 * s * k * (1 - k) : 0; }
      // Does it drop into the open top, or hit the rim?
      if (ball.vy > 0 && prevY < m.y && ball.y >= m.y) {
        const off = Math.abs(ball.x - m.x);
        past = true;
        if (off < CATCH * s && !fumble) { B.jump = 0; B.pose.legs = 'stand'; return 'in'; }
        if (off < RIM * s) {
          rimmed = true;
          ball.vy = -Math.abs(ball.vy) * 0.45;
          ball.vx = (Math.sign(ball.x - m.x) || 1) * Math.max(Math.abs(ball.vx) * 0.5, 25 * s);
          fx.burst(ball.x, ball.y, s, ['#FFD23F', '#FFFFFF'], 5, 5, 0, 260);
          pulse(B, 'squash');
        }
      }
    }
    if (!catcher && isCurrent(gB) && B.jump > 0) B.jump = Math.max(0, B.jump - 30 * s * dt);
    if (!isCurrent(gB) || !B.anchor) past = true;            // nobody to catch it any more
    // Landing on whatever is under it (looked up every 120 ms), or leaving the screen.
    if (past && now > nextGround) { nextGround = now + 120; ground = surfaceBelow(ball.x, ball.y - 1); }
    const over = ground && (ground.ledge === FLOOR || (ball.x >= ground.g.x1 && ball.x <= ground.g.x2));
    if (ball.vy > 0 && over && ball.y >= ground.g.y - 1.5 * s) {
      file.y = ground.g.y - 1.5 * s;
      if (isCurrent(gB)) { B.jump = 0; B.pose.legs = 'stand'; }
      return { landed: ground, x: ball.x };
    }
    if (ball.x < -20 || ball.x > env.W + 20 || ball.y > env.H + 20) {
      if (isCurrent(gB)) { B.jump = 0; B.pose.legs = 'stand'; }
      return 'gone';
    }
  }
}

// ---------- the party ----------
// Clawd sets up a big speaker and a light stand, puts on sunglasses and drops the beat. Everyone
// on screen dances where they are, each under its own colored lights, until the song ends.
// Then the lights go off and Clawd packs everything away.
export const BPM = 120;
let current = null;
export const partyOn = () => (current?.live() ? current : null);
hooks.beat = now => partyOn()?.beat(now) ?? null;

// Room for the party where it stands (the speaker on one side, the lights on the other, and
// space above for the lights), or the floor spot farthest from the others (on a floor that lies
// over content, only where nothing is under it: floor.rest). Anyone standing where the party goes
// is asked to step aside.
async function partyPlace(g) {
  const me = g.me, s = env.S, room = 20 * s;
  const fits = () => {
    const a = me.anchor;
    if (!a || a.rest === 'lean') return null;
    const L = baseOf(a), q = L.geom();
    if (!q || ['text', 'caret', 'pill', 'chair', 'bed'].includes(L.kind) || q.y < (STAND.rows + 3) * s) return null;
    const right = L === FLOOR ? castleLeft() - 4 * s : q.x2;
    if (L === FLOOR && !restsOnFloor(me.x - room, me.x + room)) return null;
    return me.x - q.x1 > room && right - me.x > room ? { L, q } : null;
  };
  let here = fits();
  if (!here) {
    const lo = 12 * s + room, hi = castleLeft() - room;
    if (hi < lo) return null;
    const pieces = floorRest() && floorStretches(lo - room, hi + room, 2 * room).map(([a, b]) => [a + room, b - room]);
    if (pieces && !pieces.length) return null;
    const somewhere = pieces ? () => { const [a, b] = weighted(pieces, ([p, q]) => q - p + 1); return rand(a, b); } : () => rand(lo, hi);
    const others = cast.filter(o => o !== me && o.alpha > 0 && Math.abs(o.y - floorY()) < 4 * s).map(o => o.x);
    let best = somewhere(), bestGap = -1;
    for (let i = 0; i < 12; i++) {
      const x = somewhere(), gap = Math.min(Infinity, ...others.map(o => Math.abs(o - x)));
      if (gap > bestGap) { best = x; bestGap = gap; }
    }
    await travelTo({ ledge: FLOOR, dx: pieces ? best - FLOOR.geom().x1 : best, rest: 'stand' }, g);
    here = fits();
    if (!here) return null;
  }
  standHere(me);
  hooks.makeRoom?.([me], room + 8 * s);
  const { L, q } = here, dir = chance(0.5) ? 1 : -1;
  return { ledge: L, dir, speakerDx: me.x + dir * 13 * s - q.x1, standDx: me.x - dir * 12 * s - q.x1 };
}

// invite(party) is called when the beat drops, to bring in the others.
export async function party(g, invite) {
  const me = g.me, p = me.pose, s = env.S;
  const at = await partyPlace(g);
  const geo = at?.ledge.geom();
  if (!geo) return;
  const toward = d => (d > 0 ? 'right' : 'left');
  // The speaker comes out on one side, with a heavy thud...
  p.eyes = toward(at.dir);
  const speaker = { kind: 'speaker', owner: me, ledge: at.ledge, dx: at.speakerDx, x: geo.x1 + at.speakerDx, y: geo.y, s, reveal: 0 };
  await unfold(g, 'speaker', speaker, SPEAKER_ROWS);
  fx.puff(speaker.x, speaker.y, s); pulse(me, 'squash');
  await wait(300, g);
  // ...the lights on the other, one lamp after the other.
  p.eyes = toward(-at.dir);
  const stand = { kind: 'stand', owner: me, ledge: at.ledge, dx: at.standDx, x: geo.x1 + at.standDx, y: geo.y, s, reveal: 0, lit: 0 };
  await unfold(g, 'stand', stand, STAND.rows);
  for (let i = 1; i <= STAND.lamps.length; i++) {
    stand.lit = i;
    const [c, r] = STAND.lamps[i - 1];
    fx.star(stand.x + (c + 1) * s, stand.y + (r + 1) * s, 380);
    await wait(260, g);
  }
  // Sunglasses on, a press on the speaker, and the beat drops.
  p.eyes = 'center';
  p.armR = 'up'; await wait(250, g);
  p.glasses = true; p.armR = 'side';
  fx.star(me.x - 4 * me.s, me.y - 7 * me.s, 450);
  await wait(300, g);
  const key = at.dir > 0 ? 'armR' : 'armL';
  p.eyes = toward(at.dir);
  p[key] = 'reach'; await wait(280, g); p[key] = 'side';
  const t0 = clockNow(), beatMs = 60000 / BPM, bars = Math.floor(rand(10, 15));
  const P = {
    host: me, speaker, stand, t0, beatMs, on: true,
    beat: now => { const k = Math.max(0, now - t0); return { n: Math.floor(k / beatMs), f: (k % beatMs) / beatMs }; },
    live: () => P.on && isCurrent(g),
  };
  speaker.party = stand.party = current = P;
  fx.burst(speaker.x, speaker.y - 16 * s, s, PARTY_COLORS, 20, 16, 24, 900);
  invite?.(P);
  await dance(g, P, bars * 4);
  // The song ends: the lights go off and everything is packed away.
  P.on = false;
  stand.lit = 0;
  p.glasses = true;                                       // the dance's end reset its pose; it takes them off by hand
  await wait(500, g);
  p.armR = 'up'; await wait(250, g); p.glasses = false; p.armR = 'side';
  p.eyes = toward(-at.dir);
  await fold(g, 'stand', STAND.rows);
  p.eyes = toward(at.dir);
  await fold(g, 'speaker', SPEAKER_ROWS);
  p.eyes = 'center';
  for (let k = 0; k < 3; k++) fx.heart(me.x + rand(-5, 5) * s, me.y - 9 * s, s);
  await happyHop(g, 2);
}

// A guest hears the music and dances along until it stops.
export async function danceAlong(g, P) {
  const me = g.me;
  if (!P.live()) return;
  me.dancing = true;
  me.pose.eyes = 'wide'; pulse(me, 'stretch');
  await wait(rand(150, 500), g);
  await dance(g, P);
  me.dancing = false;
  fx.heart(me.x, me.y - 9 * me.s, me.s);
  await happyHop(g, 1);
}

// ---------- dancing ----------
// Every bar (4 beats) a dancer picks a new move. A move sets the pose for one moment of a beat:
// b = { n, f } is the beat number and how far into it (0..1); on is true on its first frame.
const MOVES = {
  bounce(me, b, on) {
    if (on) pulse(me, 'squash');
    me.jump = 1.6 * me.s * hump(b.f);
    me.pose.armL = me.pose.armR = b.n % 2 ? 'up' : 'side';
  },
  wave(me, b) {                         // arms up in turn, a dip on every beat
    me.pose.armL = b.n % 2 ? 'up' : 'side';
    me.pose.armR = b.n % 2 ? 'side' : 'up';
    me.bob = b.f < 0.25 ? Math.max(1, Math.round(me.s * 0.6)) : 0;
  },
  sway(me, b) {                         // arms up, feet apart, swaying side to side
    Object.assign(me.pose, { armL: 'up', armR: 'up', legs: 'spread' });
    me.rotAround = 'feet';
    me.rot = 0.14 * Math.sin(Math.PI * (b.n + b.f));
  },
  step(me, b, on, st) {                 // little steps left and right
    me.anchor.dx = st.baseDx + 3 * me.s * Math.sin(Math.PI * (b.n + b.f) / 2);
    me.pose.legs = b.f < 0.5 ? 'walkB' : 'walkA';
    me.pose.armL = b.n % 2 ? 'up' : 'side';
    me.pose.armR = b.n % 2 ? 'side' : 'up';
  },
  turn(me, b, on) {                     // front, turned right, front, turned left
    if (on) {
      const k = b.n % 4;
      if (k % 2 === 0) setShape(me, 'normal', 'front'); else setShape(me, 'side', 'side', k === 1 ? 1 : -1);
    }
    me.jump = me.s * hump(b.f);
  },
  jumpy(me, b, on) {                    // small bounces, then a big jump on the fourth beat
    const big = b.n % 4 === 3;
    if (on && big) pulse(me, 'squash');
    me.jump = (big ? 3.6 : 0.8) * me.s * hump(b.f);
    me.pose.armL = me.pose.armR = big && b.f > 0.2 ? 'up' : 'side';
  },
  spin(me, b) {                         // for rollers: a full spin on every other beat
    if (b.n % 2 === 0) {
      Object.assign(me.pose, { legs: 'none', armL: 'none', armR: 'none' });
      me.rotAround = 'center';
      me.rot = 2 * Math.PI * easeInOut(b.f);
    } else {
      me.rot = 0; me.rotAround = 'feet';
      Object.assign(me.pose, { legs: 'stand', armL: 'up', armR: 'up' });
      me.jump = 1.2 * me.s * hump(b.f);
    }
  },
  chomp(me, b) {                        // for the folder: open and shut to the beat, like singing
    me.pose.variant = b.f < 0.5 ? 'open' : null;
    me.jump = 0.8 * me.s * hump(b.f);
    me.pose.armL = me.pose.armR = b.n % 2 ? 'side' : 'up';
  },
  pulse(me, b) {                        // its "pulse" look on every beat (Spotify's sound waves swelling), a dip and a fist pump
    me.pose.variant = b.f < 0.35 ? 'pulse' : null;
    me.bob = b.f < 0.25 ? Math.max(1, Math.round(me.s * 0.6)) : 0;
    me.pose.armL = b.n % 2 ? 'up' : 'side';
    me.pose.armR = b.n % 2 ? 'side' : 'up';
  },
};

// The moves it may dance, with how often. Step needs room around it; spin is for rollers, chomp
// for companions that open up and pulse for ones with a "pulse" look (catalog.js says who has
// which). With every move switched off it still bounces, since a dancer has to do something.
export function movesFor(me, roomy) {
  const list = [['bounce', 3], ['wave', 2], ['sway', 2], ['turn', 1.5], ['jumpy', 1.5], ['step', roomy ? 2 : 0], ['spin', 4], ['chomp', 4], ['pulse', 4]]
    .filter(([m, w]) => w > 0 && can(me, `dance.${m}`));
  return list.length ? list : [['bounce', 1]];
}

// Back to standing still, between moves and at the end.
function calm(me, st) {
  me.rot = 0; me.rotAround = 'feet'; me.jump = 0; me.bob = 0;
  Object.assign(me.pose, { legs: 'stand', armL: 'side', armR: 'side', variant: null });
  setShape(me, 'normal', 'front');
  if (me.anchor && st.baseDx !== undefined) me.anchor.dx = st.baseDx;
}

// The beat `tempo` times as fast (slower, below 1): { n, f } as P.beat gives it.
const slowBeat = (b, tempo) => { const at = (b.n + b.f) * tempo; return { n: Math.floor(at), f: at - Math.floor(at) }; };

// only: one move for the whole dance instead of a new one every bar (for the gallery).
export async function dance(g, P, endBeat = Infinity, only = null) {
  const me = g.me;
  standHere(me);
  const a = me.anchor, geo = a?.ledge.geom();
  const roomy = !!(geo && a.dx !== undefined && a.dx > 6 * me.s && geo.x2 - geo.x1 - a.dx > 6 * me.s);
  const st = { baseDx: a?.dx };
  const light = { kind: 'partyLight', owner: me, of: me, party: P, hue: Math.floor(rand(0, PARTY_COLORS.length)) };
  props.add(light);
  // A slowpoke dances in slow motion: its moves go on a beat of its own, that much slower
  // ("danceTempo" in its moves: the sloth's), the host's lights and notes still on the music's.
  const tempo = me.design.moves?.danceTempo ?? 1;
  let bar = -1, move = null, lastBeat = -1, lastStep = -1;
  while (P.live()) {
    await tick(g);
    const b = P.beat(clockNow());
    if (b.n >= endBeat) break;
    const on = b.n !== lastBeat;
    lastBeat = b.n;
    const step = tempo === 1 ? b : slowBeat(b, tempo), onStep = step.n !== lastStep;
    lastStep = step.n;
    if (Math.floor(step.n / 4) !== bar) {
      bar = Math.floor(step.n / 4);
      calm(me, st);
      const moves = movesFor(me, roomy), fresh = moves.filter(([m]) => m !== move);   // a different move each bar, if it has one
      move = only || weighted(fresh.length ? fresh : moves, m => m[1])[0];
      me.pose.eyes = chance(0.4) ? 'closed' : 'center';
    }
    MOVES[move](me, step, onStep, st);
    if (on && me === P.host) partyEffects(P, b);
  }
  calm(me, st);
  props.delete(light);
  if (me.anchor) applyRest(me, me.anchor);
}

// On the beat: music notes from the speaker, and sparkles off the mirror ball.
function partyEffects(P, b) {
  const sp = P.speaker, st = P.stand, s = sp.s;
  if (b.n % 2 === 0) {
    const color = PARTY_COLORS[(b.n / 2) % PARTY_COLORS.length];
    const art = NOTES[b.n % 4 === 0 ? 0 : 1].map(([c, r]) => [c, r, color]);
    fx.emit({ kind: 'sprite', art, x: sp.x + rand(-3, 3) * s, y: sp.y - (SPEAKER_ROWS + 1) * s, vx: rand(-4, 4) * s, vy: -9 * s,
      life: 1500, scale: Math.max(1, Math.round(s * 0.6)), wobble: true });
  }
  const bx = st.x + STAND.ball.c * s, by = st.y + STAND.ball.r * s;
  for (let i = 0; i < 3; i++) {
    fx.emit({ kind: 'square', x: bx, y: by, vx: rand(-30, 30) * s, vy: rand(-6, 14) * s, life: rand(400, 700), size: 0.5,
      color: chance(0.5) ? '#FFFFFF' : PARTY_COLORS[Math.floor(rand(0, PARTY_COLORS.length))], fade: 0.9 });
  }
}
